import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";

export default function MessagesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("with") || null;

  const threads = useQuery({ queryKey: ["threads"], queryFn: async () => (await api.get("/messages/threads")).data });

  const openThread = (otherId) => setParams({ with: otherId });

  return (
    <div className="container-fl py-10" data-testid="messages-page">
      <div className="overline mb-2">Inbox</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mb-8">Messages</h1>
      <div className="grid md:grid-cols-[300px_1fr] gap-4 border border-neutral-200 rounded-xl bg-white min-h-[560px]">
        <aside className="border-r border-neutral-200 overflow-y-auto">
          {(threads.data || []).length === 0 ? (
            <div className="p-8 text-center text-neutral-500 text-sm">No messages yet.</div>
          ) : (threads.data || []).map((t) => (
            <button key={t._id} data-testid={`thread-${t.other_id}`} onClick={() => openThread(t.other_id)}
                    className={`w-full text-left p-4 border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${activeId === t.other_id ? "bg-neutral-50" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{t.other_name}</div>
                {t.unread > 0 && <span className="rounded-full bg-emerald-600 text-white text-xs px-2 py-0.5">{t.unread}</span>}
              </div>
              <div className="text-xs text-neutral-500 truncate mt-1">{t.last_message}</div>
            </button>
          ))}
        </aside>
        <section className="flex flex-col">
          {activeId ? (
            <ChatPane otherId={activeId} me={user} onSent={() => qc.invalidateQueries({ queryKey: ["threads"] })}/>
          ) : (
            <div className="flex-1 grid place-items-center text-neutral-500 text-sm p-8 text-center">
              <div>
                <MessageSquare size={32} className="mx-auto text-neutral-300 mb-2"/>
                Pick a conversation from the list.
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ChatPane({ otherId, me, onSent }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const endRef = useRef(null);
  const q = useQuery({
    queryKey: ["thread", otherId],
    queryFn: async () => (await api.get(`/messages/thread/${otherId}`)).data,
    refetchInterval: 5000,
  });

  const send = useMutation({
    mutationFn: (text) => api.post("/messages", { other_user_id: otherId, body: text }),
    onSuccess: () => { setBody(""); qc.invalidateQueries({ queryKey: ["thread", otherId] }); onSent?.(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Send failed"),
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [q.data]);

  const submit = (e) => {
    e.preventDefault();
    if (body.trim()) send.mutate(body.trim());
  };

  return (
    <>
      <div className="flex-1 p-4 overflow-y-auto max-h-[500px] space-y-2" data-testid="chat-pane">
        {(q.data || []).map((m) => {
          const mine = m.from_id === me.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-emerald-600 text-white" : "bg-neutral-100 text-neutral-900"}`}>
                {m.body}
                <div className={`text-[10px] mt-1 ${mine ? "text-emerald-100" : "text-neutral-500"}`}>
                  {new Date(m.created_at).toLocaleString("en-IE", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>
      <form onSubmit={submit} className="border-t border-neutral-200 p-3 flex gap-2">
        <input data-testid="chat-input" value={body} onChange={(e) => setBody(e.target.value)}
               placeholder="Type a message…" className="flex-1 h-11 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
        <button data-testid="chat-send" type="submit" disabled={!body.trim() || send.isPending} className="btn-accent"><Send size={14}/></button>
      </form>
    </>
  );
}
